import React from "react"
import { Breadcrumb, BreadcrumbProps } from "antd"
import { useNavigate } from "react-router-dom"

import { useProjectStore } from "stores/projectStore"
import { useUserStore } from "stores/userStore"
import {
  listTargetFor,
  openFolderInList,
} from "features/Projects/utils/projectListState"

import { folderAncestors } from "utils/projectTree"

type CrumbItem = NonNullable<BreadcrumbProps["items"]>[number]

/** Folders shown in full; a deeper path folds its middle folders into "…". */
const MAX_FOLDERS = 2

const folderName = (path: string): string =>
  path.slice(path.lastIndexOf(".") + 1)

/**
 * Renders the breadcrumb navigation for a project: Projects / each folder of
 * the project's folder path / the project name (the page title).
 *
 * "Projects" opens the project list at the project's own folder; each folder
 * opens it at that folder. Both use the view that lists the project. With
 * more than two folders the middle ones fold into a "…" menu, and each folder
 * name is capped, so the folders give way before the project name does.
 *
 * @component
 * @returns The rendered ProjectBreadcrumb component.
 */
const ProjectBreadcrumb: React.FC = () => {
  const navigate = useNavigate()
  const project = useProjectStore((state) => state.project)
  const username = useUserStore((state) => state.user.username)

  const target = listTargetFor(project, username)
  const openFolder = (folder: string): void =>
    openFolderInList(navigate, { ...target, folder })

  // Real links (to the list), so they take the keyboard; a click opens the
  // list at the folder in place.
  const linkTo =
    (folder: string) =>
    (event: React.MouseEvent<HTMLElement>): void => {
      event.preventDefault()
      openFolder(folder)
    }

  const folderCrumb = (path: string): CrumbItem => ({
    key: path,
    href: "/home",
    onClick: linkTo(path),
    title: (
      <span
        className="inline-block max-w-[8rem] truncate align-bottom"
        title={folderName(path)}
      >
        {folderName(path)}
      </span>
    ),
  })

  const folders = folderAncestors(target.folder)
  const folded = folders.length > MAX_FOLDERS ? folders.slice(1, -1) : []
  const folderItems: CrumbItem[] =
    folded.length === 0
      ? folders.map(folderCrumb)
      : [
          folderCrumb(folders[0]),
          // The middle folders, one click away
          {
            key: "folded",
            title: (
              <button
                type="button"
                aria-label="More folders"
                className="cursor-pointer border-0 bg-transparent p-0"
              >
                …
              </button>
            ),
            menu: {
              items: folded.map((path) => ({
                key: path,
                label: folderName(path),
              })),
              onClick: ({ key }) => openFolder(key),
            },
            dropdownProps: { trigger: ["click"] },
          },
          folderCrumb(folders[folders.length - 1]),
        ]

  return (
    <Breadcrumb
      className="flex items-center"
      items={[
        // The project list, opened at the project's folder
        {
          key: "projects",
          title: "Projects",
          href: "/home",
          onClick: linkTo(target.folder),
        },
        ...folderItems,
        // The current project: the page title, so it reads as one line,
        // truncated (full name in the tooltip) once the folders have given way
        {
          key: "project",
          className: "block truncate",
          title: (
            <h1
              className="inline text-lg font-semibold text-gray-900"
              title={project.Name}
            >
              {project.Name}
            </h1>
          ),
        },
      ]}
    />
  )
}

export default ProjectBreadcrumb
